import React from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dynamic from 'next/dynamic';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { Header } from 'src/dispensary/core-menu/components/header';
import { KioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout';
import CoreMenuHeader from 'dispensary/core-menu/components/core-menu-header';
import CoreMenuFooter from 'dispensary/core-menu/components/core-menu-footer';
import ROUTES from 'src/routes';
import { DesktopOnly } from 'shared/components';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import usePaths from 'hooks/use-paths';
import useUser from 'hooks/use-user';
import useFeatureFlags from 'hooks/use-feature-flags';

import { useCheckAgeVerification } from 'src/dispensary/core-menu/age-verification-gate/hooks';

import { LayoutWrapper } from './layout-wrapper';

const AgeVerificationGateLayout = dynamic<{
  children: React.ReactNode;
}>(() => import('./age-verification-gate-layout').then((mod) => mod.AgeVerificationGateLayout), {
  ssr: false,
});

type CoreMenuLayoutProps = {
  children: React.ReactNode;
};

export const CoreMenuLayout = observer(({ children }: CoreMenuLayoutProps): JSX.Element | null => {
  const { dispensary } = useDispensary();
  const FeatureFlags = useFeatureFlags();
  const router = useRouter();
  const { route } = usePaths();
  const user = useUser();
  const { isAgeVerified, loading } = useCheckAgeVerification({
    dispensary,
    userId: user?.id,
  });
  const flags = useFlags();
  const isNewEmbeddedHeaderFlagEnabled = flags['growth.ecomm.embedded-header.rollout'] ?? false;
  const isKioskProEnabled = useKioskPro();
  const { onCollectionsPage, isKiosk } = useUI();

  if (!dispensary || !FeatureFlags.hasTargetDispensary) {
    return null;
  }

  if (!loading && !isAgeVerified) {
    return <AgeVerificationGateLayout children={children} />;
  }

  const isProductPage = router.route === `${route}${ROUTES.PRODUCT_ID}`;
  const hideCategories =
    isKioskProEnabled || !(router.route.includes('products') || router.route.includes('brands') || onCollectionsPage);
  const hideFooter = isKioskProEnabled && isKiosk;

  const OldHeaderComponent = isProductPage ? (
    <DesktopOnly display='flex'>
      <CoreMenuHeader dispensary={dispensary} />
    </DesktopOnly>
  ) : (
    <CoreMenuHeader dispensary={dispensary} />
  );

  if (isKioskProEnabled && isKiosk) {
    return (
      <KioskProLayout dispensary={dispensary} hideCategories={hideCategories} hideFooter={hideFooter}>
        {children}
      </KioskProLayout>
    );
  }

  return (
    <LayoutWrapper>
      {isNewEmbeddedHeaderFlagEnabled ? <Header dispensary={dispensary} /> : OldHeaderComponent}

      {children}

      <CoreMenuFooter hideCategories={hideCategories} />
    </LayoutWrapper>
  );
});
